import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Container from '../../Container/Container';
import { get, ImageUrl, post, postFormData } from '../../ApiServices/ApiServices';
import { toast, ToastContainer } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { setProfile } from '../../redux/profileSlice';
import Loader from '../Component/Loader';

function Settings() {
    const [selectedImage, setSelectedImage] = useState(null);
    const [image, setImage] = useState(null);
    const [isProfileLoading, setIsProfileLoading] = useState(false);
    const [isPasswordLoading, setIsPasswordLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const dispatch = useDispatch();

    const validationSchema = Yup.object({
        firstName: Yup.string()
            .required('First Name is required')
            .max(50, 'First Name must be at most 50 characters'),
        lastName: Yup.string()
            .required('Last Name is required')
            .max(50, 'Last Name must be at most 50 characters'),
        email: Yup.string()
            .email('Invalid email address')
            .required('Email is required'),
    });

    const PasswordvalidationSchema = Yup.object({
        currentPassword: Yup.string()
            .required('Current password is required')
            .min(5, 'Current password must be at least 5 characters long'),
        newPassword: Yup.string()
            .required('New password is required')
            .min(5, 'New password must be at least 5 characters long'),
        confirmPassword: Yup.string()
            .required('Confirm password is required')
            .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
    });

    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setIsProfileLoading(true);
            try {
                const formData = new FormData();
                formData.append('firstName', values.firstName);
                formData.append('lastName', values.lastName);
                formData.append('email', values.email);
                if (selectedImage instanceof File) {
                    formData.append('image', selectedImage);
                }

                const endpoint = "edit-profile";
                const response = await postFormData(endpoint, formData);
                await ViewProfileupdate();

                toast.success(response?.message, {
                    position: "top-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    closeButton: false,
                });
            } catch (error) {
                toast.error(error?.message || 'Failed to update profile', {
                    position: "top-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    closeButton: false,
                });
            } finally {
                setIsProfileLoading(false);
            }
        },
    });

    const passFormic = useFormik({
        initialValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
        validationSchema: PasswordvalidationSchema,
        onSubmit: async (values) => {
            setIsPasswordLoading(true);
            try {
                const endpoint = "change-password";
                const response = await post(endpoint, values);

                toast.success(response?.message, {
                    position: "top-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    closeButton: false,
                });

                passFormic.resetForm();
            } catch (err) {
                toast.error(err?.message || 'Failed to change password', {
                    position: "top-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    closeButton: false,
                });
            } finally {
                setIsPasswordLoading(false);
            }
        },
    });

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedImage(file);
        }
    };

    const ViewProfile = async () => {
        try {
            setIsInitialLoading(true);
            const api = `view-admin-profile`;
            const totalData = await get(api);

            if (totalData?.status === true) {
                setImage(totalData?.data?.avatar);
                formik.setValues({
                    firstName: totalData?.data?.firstName,
                    lastName: totalData?.data?.lastName,
                    email: totalData?.data?.email,
                });
            }
        } catch (err) {
            console.error('Error fetching profile data:', err);
            toast.error('Failed to load profile data', {
                position: "top-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                closeButton: false,
            });
        } finally {
            setIsInitialLoading(false);
        }
    };

    const ViewProfileupdate = async () => {
        try {
            const api = `view-admin-profile`;
            const totalData = await get(api);

            if (totalData?.status === true) {
                dispatch(setProfile(totalData?.data));
            }
        } catch (err) {
            console.error('Error fetching profile data:', err);
        }
    };

    useEffect(() => {
        ViewProfile();
    }, []);

    if (isInitialLoading) {
        return (
            <Container>
                <div className="nk-content">
                    <div className="container-fluid">
                        <div className="nk-content-inner">
                            <div className="nk-content-body">
                                <div className="nk-block-head nk-block-head-sm">
                                    <div className="nk-block-between">
                                        <div className="nk-block-head-content">
                                            <h3 className="nk-block-title page-title">Settings</h3>
                                        </div>
                                    </div>
                                </div>
                                <Loader />
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        );
    }

    return (
        <Container>
            <div className="nk-content">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">
                            <div className="nk-block-head nk-block-head-sm pbZeroHeadin">
                                <div className="nk-block-between">
                                    <div className="nk-block-head-content">
                                        <h3 className="nk-block-title page-title">Settings</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="card card-full">
                                <div className="card-inner">
                                    <form onSubmit={formik.handleSubmit}>
                                        <div className="card-title-group mb-3">
                                            <div className="card-title">
                                                <h6 className="title">Profile Edit</h6>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-3">
                                                <div className="avatar-upload">
                                                    <div className="avatar-edit">
                                                        <input
                                                            type='file'
                                                            id="imageUpload" accept=".png, .jpg, .jpeg"
                                                            onChange={handleImageChange}
                                                        />
                                                        <label htmlFor="imageUpload"></label>
                                                    </div>
                                                    <div className="avatar-preview">
                                                        <div
                                                            id="imagePreview"
                                                            style={{
                                                                backgroundImage: selectedImage
                                                                    ? `url(${URL.createObjectURL(selectedImage)})`
                                                                    : image
                                                                        ? `url(${ImageUrl}/${image})`
                                                                        : `url(assets/images/no-photo.png)`
                                                            }}
                                                        >
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-9">
                                                <div className="row inputCardBox">
                                                    <div className="col-md-5 mb-3">
                                                        <label>First Name</label>
                                                        <input
                                                            className={`form-control ${formik.touched.firstName && formik.errors.firstName ? 'is-invalid' : ''}`}
                                                            type="text"
                                                            placeholder="Enter first name"
                                                            {...formik.getFieldProps('firstName')}
                                                        />
                                                        {formik.touched.firstName && formik.errors.firstName ? (
                                                            <div className="invalid-feedback">{formik.errors.firstName}</div>
                                                        ) : null}
                                                    </div>
                                                    <div className="col-md-5 mb-3">
                                                        <label>Last Name</label>
                                                        <input
                                                            className={`form-control ${formik.touched.lastName && formik.errors.lastName ? 'is-invalid' : ''}`}
                                                            type="text"
                                                            placeholder="Enter last name"
                                                            {...formik.getFieldProps('lastName')}
                                                        />
                                                        {formik.touched.lastName && formik.errors.lastName ? (
                                                            <div className="invalid-feedback">{formik.errors.lastName}</div>
                                                        ) : null}
                                                    </div>
                                                    <div className="col-md-5 mb-3">
                                                        <label>E-mail</label>
                                                        <input
                                                            className={`form-control ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                                                            type="text"
                                                            placeholder="Enter e-mail"
                                                            {...formik.getFieldProps('email')}
                                                        />
                                                        {formik.touched.email && formik.errors.email ? (
                                                            <div className="invalid-feedback">{formik.errors.email}</div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row mt-3">
                                            <div className="col-md-12">
                                                <button
                                                    type="submit"
                                                    className="addBtnCustomer saveBtn"
                                                    // disabled={isProfileLoading}
                                                    style={isProfileLoading ? { pointerEvents: "none", width: "180px" } : { width: "180px" }} >

                                                    {isProfileLoading ? (
                                                        <>
                                                            <span class="spinner"></span> Save & Update
                                                        </>
                                                    ) : (
                                                        "Save & Update"
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </form>

                                    <form onSubmit={passFormic.handleSubmit}>
                                        <div className="card-title-group mb-3 mt-5">
                                            <div className="card-title">
                                                <h6 className="title">Change Password</h6>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-12">
                                                <div className="row inputCardBox">
                                                    <div className="col-md-4 mb-3">
                                                        <label>Current Password</label>
                                                        <input
                                                            className={`form-control ${passFormic.touched.currentPassword && passFormic.errors.currentPassword ? 'is-invalid' : ''}`}
                                                            placeholder="Enter current password"
                                                            type="password"
                                                            name="currentPassword"
                                                            value={passFormic.values.currentPassword}
                                                            onChange={passFormic.handleChange}
                                                            onBlur={passFormic.handleBlur}
                                                        />
                                                        {passFormic.touched.currentPassword && passFormic.errors.currentPassword && (
                                                            <div className="error">{passFormic.errors.currentPassword}</div>
                                                        )}
                                                    </div>
                                                    <div className="col-md-8"></div>


                                                    <div className="col-md-4 mb-3">
                                                        <label>New Password</label>
                                                        <input
                                                            className={`form-control ${passFormic.touched.newPassword && passFormic.errors.newPassword ? 'is-invalid' : ''}`}
                                                            placeholder="Enter new password"
                                                            type="password"
                                                            name="newPassword"
                                                            value={passFormic.values.newPassword}
                                                            onChange={passFormic.handleChange}
                                                            onBlur={passFormic.handleBlur}
                                                        />
                                                        {passFormic.touched.newPassword && passFormic.errors.newPassword && (
                                                            <div className="error">{passFormic.errors.newPassword}</div>
                                                        )}
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label>Confirm Password</label>
                                                        <input
                                                            className={`form-control ${passFormic.touched.confirmPassword && passFormic.errors.confirmPassword ? 'is-invalid' : ''}`}
                                                            placeholder="Confirm new password"
                                                            type="password"
                                                            name="confirmPassword"
                                                            value={passFormic.values.confirmPassword}
                                                            onChange={passFormic.handleChange}
                                                            onBlur={passFormic.handleBlur}
                                                        />
                                                        {passFormic.touched.confirmPassword && passFormic.errors.confirmPassword && (
                                                            <div className="error">{passFormic.errors.confirmPassword}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row mt-1">
                                            <div className="col-md-12">
                                                <button
                                                    type="submit"
                                                    className="addBtnCustomer saveBtn"
                                                    // disabled={isPasswordLoading}

                                                    style={isPasswordLoading ? { pointerEvents: "none", width: "180px" } : { width: "180px" }} >

                                                    {isPasswordLoading ? (
                                                        <>
                                                            <span class="spinner"></span> Save & Update
                                                        </>
                                                    ) : (
                                                        "Save & Update"
                                                    )}
                                                </button>

                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    );
}

export default Settings;